import { useEffect, useRef } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "../context/LanguageContext";
import { EASE } from "./motion";

const Tile = ({ tile, index, active, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 12 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 8 }}
    transition={{ delay: 0.04 + index * 0.035, duration: 0.38, ease: EASE }}
  >
    <NavLink
      to={tile.to}
      onClick={onClose}
      data-testid={`mobile-nav-link-${tile.key}`}
      className={`group relative block h-[74px] overflow-hidden border outline-none transition-colors duration-300 focus-visible:border-wave sm:h-20 ${
        active ? "border-wave/80" : "border-white/10 hover:border-white/25"
      }`}
    >
      <img src={tile.img} alt="" className="absolute inset-0 h-full w-full object-cover" style={{ objectPosition: tile.position }} loading="lazy" />
      <span className={`absolute inset-0 transition-colors duration-300 ${active ? "bg-ink/35" : "bg-ink/50 group-hover:bg-ink/40"}`} aria-hidden="true" />
      {active && <span className="absolute left-0 top-0 h-full w-[3px] bg-wave" aria-hidden="true" />}
      <span className="relative z-10 flex h-full items-center px-5 text-[13px] font-medium uppercase tracking-[0.22em] text-white sm:px-6">
        {tile.label}
      </span>
    </NavLink>
  </motion.div>
);

export const MobileMenu = ({ open, onClose, goContact }) => {
  const { t } = useLanguage();
  const location = useLocation();
  const panelRef = useRef(null);

  const tiles = [
    ...t.focus.items.map((item) => ({
      key: item.to.slice(1),
      to: item.to,
      img: item.img,
      label: item.short,
      position: "center",
    })),
    {
      key: "about",
      to: "/ueber-mich",
      img: "/images/david-portrait-new.jpg",
      label: t.nav.about,
      position: "center 28%",
    },
  ];

  useEffect(() => {
    if (!open) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.__lenis?.stop();
    panelRef.current?.focus({ preventScroll: true });

    const onKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.__lenis?.start();
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={panelRef}
          data-testid="mobile-menu-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t.nav.menu}
          tabIndex={-1}
          data-lenis-prevent
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.32, ease: "easeOut" }}
          className="fixed inset-0 z-40 overflow-y-auto overscroll-contain bg-ink px-5 pb-6 pt-24 outline-none sm:px-8 lg:hidden"
        >
          <nav className="mx-auto flex w-full max-w-[620px] flex-col gap-2.5" aria-label="Mobile">
            {tiles.map((tile, index) => (
              <Tile key={tile.key} tile={tile} index={index} active={location.pathname === tile.to} onClose={onClose} />
            ))}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ delay: 0.04 + tiles.length * 0.035, duration: 0.38, ease: EASE }}
            >
              <a
                href="/#kontakt"
                onClick={goContact}
                data-testid="mobile-nav-contact"
                className="flex h-[74px] items-center bg-wave px-5 text-[13px] font-medium uppercase tracking-[0.22em] text-ink outline-none transition-colors duration-300 hover:bg-mist focus-visible:outline focus-visible:outline-2 focus-visible:outline-wave sm:h-20 sm:px-6"
              >
                {t.nav.contact}
              </a>
            </motion.div>
          </nav>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
